
import { Vue, Component, Prop } from "vue-property-decorator";
import UiButton from "@/components/button/ui-button.vue";
import CircleWithInitials from "@/components/card/CircleWithInitials.vue";
import ExpandDownArrow from "@/components/icons/ExpandDownArrow.vue";
import ConversationDetailsModal from "@/components/inbox/ConversationDetailsModal.vue";
import ConversationUserData from "@/entities/ConversationUserData";
import ConversationParticipants from "@/entities/ConversationParticipants";
import DELETE_USER_FROM_CONVERSATION from "@/apollo/conversations/DeleteUserFromConversation.gql";
import { Conversation } from "@/generated/graphql";

import TooltipPlacement from "@/entities/TooltipPlacement";
import StatusChip from "@/components/icons/StatusChip.vue";
import ChipStatus from "@/entities/ChipStatus"

@Component({
  components: {
    StatusChip,
    UiButton,
    CircleWithInitials,
    ExpandDownArrow,
    ConversationDetailsModal,
  }
})
export default class InboxWindowHead extends Vue {
  @Prop({ type: Boolean, default: true }) readonly showUsers!: boolean;
  @Prop({ type: String, required: true }) readonly currentUserId!: string;
  @Prop({ type: Boolean, default: false }) readonly isGroupConversation!: boolean;
  @Prop({ type: Object, required: true }) readonly conversation!: Conversation;
  @Prop({ type: Boolean, default: false }) readonly noParticipants!: boolean;
  @Prop({ type: Object, default: undefined }) readonly onlyParticipant?: ConversationUserData;
  @Prop({ type: Object, default: undefined }) readonly participants?: ConversationParticipants ;

  conversationDetailsModal: boolean = false;
  onlyParticipantLeft: boolean = false;
  participantsLimit: number = 3;
  tooltipPlacement = TooltipPlacement;
  ChipStatus: typeof ChipStatus = ChipStatus;

  get titleCursor(): string {
    return this.isGroupConversation ? 'cursor-pointer' : 'cursor-default';
  }

  deletedParticipantCircleStyle(participant: ConversationUserData): string {
    return participant.deleted ? 'opacity-25': 'opacity-100';
  }

  get onlyParticipantLeftStyle(): string {
    return this.noParticipants
      ? "opacity-50"
      : "opacity-100";
  }

  get participantsMoreThanLimit(): boolean {
    if (!this.participants) {
      return false;
    }
    const participants = this.participants?.all.length;
    return participants! > this.participantsLimit;
  }

  get participantsForCircles(): ConversationUserData[] {
    if (this.participantsMoreThanLimit) {
      return this.participants!.all.slice(0, 3);
    }
    return this.participants!.all;
  }

  get participantsRest(): string | undefined {
    if (!this.participantsMoreThanLimit) {
      return undefined;
    }
    const diff = this.participants?.active.length! - this.participantsLimit;
    return diff > 0
      ? diff.toString()
      : undefined;
  }

  get conversationTitle(): string | undefined {
    if (!this.participants?.all.length) {
      return undefined;
    }

    if (this.conversation.name && this.noParticipants) {
      this.onlyParticipantLeft = true;
      return `${this.conversation.name} (everyone left)`;
    }

    if (this.conversation.name) {
      return this.conversation.name;
    }

    if (this.noParticipants) {
      this.onlyParticipantLeft = true;
      return `${this.onlyParticipant!.initial} left the conversation`;
    }

    return this.participants.all
      .map(participant => participant.initial)
      .join(", ");
  }

  openDetailsModal(): void {
    this.conversationDetailsModal = true;
  }

  leaveConversation(): void {
    this.$apollo.mutate({
      mutation: DELETE_USER_FROM_CONVERSATION,
      variables: {
        where: {
          _and: [
            {user_id: { _eq: this.currentUserId}},
            {conversation_id: { _eq: this.conversation.id}}
          ]
        },
        set: {deleted: true}
      }
    });
    this.$emit("conversationDeleted", this.conversation.id)
  }
}
